import { createGameStore, type PersistedGameStore } from '@game-libs/generic-game-state';
import { registerStoreReset } from './index';
import { GAME_LEVEL_IDS, type GameLevelId, isGameLevelId } from '../../types/game-levels';

// ---------------------------------------------------------------------------
// Per-level + global play stats (ticks / clicks / birds). Tolerant persist merge.
// ---------------------------------------------------------------------------

export type PerLevelPlayStats = {
  ticksCount: number;
  numOfClicks: number;
  moneyBidsKilled: number;
};

/**
 * Cross-level counters that should survive “session” resets and level switches.
 * Extra keys may appear over time; persist merge keeps unknown fields from JSON.
 */
export type GlobalPlayStats = Record<string, unknown>;

function emptyLevelStats(): PerLevelPlayStats {
  return { ticksCount: 0, numOfClicks: 0, moneyBidsKilled: 0 };
}

function defaultByLevel(): Record<GameLevelId, PerLevelPlayStats> {
  return {
    tutorial: emptyLevelStats(),
    dummy: emptyLevelStats(),
    mainGame: emptyLevelStats(),
  };
}

function normalizePerLevelStats(raw: unknown): PerLevelPlayStats {
  const base = emptyLevelStats();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return base;
  }
  const record = raw as Record<string, unknown>;
  return {
    ticksCount: typeof record.ticksCount === 'number' && Number.isFinite(record.ticksCount) ? record.ticksCount : base.ticksCount,
    numOfClicks: typeof record.numOfClicks === 'number' && Number.isFinite(record.numOfClicks) ? record.numOfClicks : base.numOfClicks,
    moneyBidsKilled:
      typeof record.moneyBidsKilled === 'number' && Number.isFinite(record.moneyBidsKilled)
        ? record.moneyBidsKilled
        : base.moneyBidsKilled,
  };
}

function normalizeGlobalStats(raw: unknown, fallback: GlobalPlayStats): GlobalPlayStats {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ...fallback };
  }
  return { ...fallback, ...(raw as GlobalPlayStats) };
}

export type GameStatsState = {
  /** Scene bucket that receives tick/click/bird increments. */
  activeLevelId: GameLevelId;
  byLevel: Record<GameLevelId, PerLevelPlayStats>;
  /** Never cleared by `resetSessionPlayStats` (REDUX reset / new run). Cleared only on full store reset + wipe. */
  global: GlobalPlayStats;
};

export type GameStatsActions = {
  setActiveLevelId: (levelId: GameLevelId) => void;
  incrementActiveLevelTick: () => void;
  incrementActiveLevelClicks: (amount: number) => void;
  incrementActiveLevelMoneyBids: (amount: number) => void;
  getActiveTicksCount: () => number;
  getActiveNumOfClicks: () => number;
  getActiveMoneyBidsKilled: () => number;
  /** Sum ticks across all levels (used for “fresh save” / e2e URL gating). */
  getTotalTicksAllLevels: () => number;
  hasNoPlayStatsAnyLevel: () => boolean;
  sync: (patch: Partial<GameStatsState>) => void;
  /** Clears per-level stats + active level; keeps `global`. */
  resetSessionPlayStats: () => void;
  /** Full factory defaults including empty `global` (clear-save / registerStoreReset). */
  reset: () => void;
};

export type GameStatsStore = GameStatsState & GameStatsActions;

const DEFAULT_GLOBAL: GlobalPlayStats = {};

const INITIAL_STATS_STATE: GameStatsState = {
  activeLevelId: 'tutorial',
  byLevel: defaultByLevel(),
  global: { ...DEFAULT_GLOBAL },
};

function mergePersistedGameStats(persisted: unknown, current: GameStatsStore): GameStatsStore {
  if (!persisted || typeof persisted !== 'object') {
    return current;
  }
  const persistedRecord = persisted as Record<string, unknown>;

  const mergedByLevel = defaultByLevel();
  const rawByLevel = persistedRecord.byLevel;
  if (rawByLevel && typeof rawByLevel === 'object' && !Array.isArray(rawByLevel)) {
    for (const levelId of GAME_LEVEL_IDS) {
      mergedByLevel[levelId] = normalizePerLevelStats((rawByLevel as Record<string, unknown>)[levelId]);
    }
  }

  const activeRaw = persistedRecord.activeLevelId;
  const activeLevelId =
    typeof activeRaw === 'string' && isGameLevelId(activeRaw) ? activeRaw : current.activeLevelId;

  const global = normalizeGlobalStats(persistedRecord.global, current.global);

  return {
    ...current,
    byLevel: mergedByLevel,
    activeLevelId,
    global,
  };
}

export const useGameStatsStore: PersistedGameStore<GameStatsStore> = createGameStore<GameStatsStore>(
  {
    saveKey: 'game-stats',
    persistConfig: {
      merge: (persisted, current) => mergePersistedGameStats(persisted, current as GameStatsStore),
    },
  },
  (set, get) => ({
    ...INITIAL_STATS_STATE,

    setActiveLevelId: (levelId) => set({ activeLevelId: levelId }),

    incrementActiveLevelTick: () => {
      const { activeLevelId, byLevel } = get();
      const slice = byLevel[activeLevelId];
      set({
        byLevel: {
          ...byLevel,
          [activeLevelId]: { ...slice, ticksCount: slice.ticksCount + 1 },
        },
      });
    },

    incrementActiveLevelClicks: (amount) => {
      const { activeLevelId, byLevel } = get();
      const slice = byLevel[activeLevelId];
      set({
        byLevel: {
          ...byLevel,
          [activeLevelId]: { ...slice, numOfClicks: slice.numOfClicks + amount },
        },
      });
    },

    incrementActiveLevelMoneyBids: (amount) => {
      const { activeLevelId, byLevel } = get();
      const slice = byLevel[activeLevelId];
      set({
        byLevel: {
          ...byLevel,
          [activeLevelId]: { ...slice, moneyBidsKilled: slice.moneyBidsKilled + amount },
        },
      });
    },

    getActiveTicksCount: () => get().byLevel[get().activeLevelId].ticksCount,
    getActiveNumOfClicks: () => get().byLevel[get().activeLevelId].numOfClicks,
    getActiveMoneyBidsKilled: () => get().byLevel[get().activeLevelId].moneyBidsKilled,

    getTotalTicksAllLevels: () =>
      GAME_LEVEL_IDS.reduce((sum, levelId) => sum + get().byLevel[levelId].ticksCount, 0),

    /** True when every per-level counter is still zero. */
    hasNoPlayStatsAnyLevel: () => {
      const { byLevel } = get();
      return GAME_LEVEL_IDS.every(
        (levelId) =>
          byLevel[levelId].ticksCount === 0 &&
          byLevel[levelId].numOfClicks === 0 &&
          byLevel[levelId].moneyBidsKilled === 0,
      );
    },

    sync: (patch) => set(patch as GameStatsStore),

    resetSessionPlayStats: () =>
      set((state) => ({
        ...state,
        activeLevelId: 'tutorial',
        byLevel: defaultByLevel(),
      })),

    reset: () =>
      set({
        activeLevelId: 'tutorial',
        byLevel: defaultByLevel(),
        global: { ...DEFAULT_GLOBAL },
      }),
  }),
);

registerStoreReset(() => useGameStatsStore.getState().reset());
